import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar as CalendarIcon, Clock, Loader2, AlertTriangle } from 'lucide-react';
import api from '../api/axios';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

export const BookingModify = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [originalBooking, setOriginalBooking] = useState(null);
  const [date, setDate] = useState(new Date());
  const [availability, setAvailability] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchBookingDetails();
  }, [token]);

  useEffect(() => {
    if (!loading && !error) {
      fetchAvailability();
    }
  }, [date, loading, error]);

  const fetchBookingDetails = async () => {
    try {
      const response = await api.get(`/bookings/details/${token}`);
      setOriginalBooking(response.data);
      setDate(new Date(response.data.date));
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Reserva no encontrada');
      setLoading(false);
    }
  };

  const fetchAvailability = async () => {
    try {
      const dateStr = date.toISOString().split('T')[0];
      const response = await api.get(`/bookings/availability?date=${dateStr}`);
      setAvailability(response.data);
    } catch (err) {
      toast.error('Error al cargar disponibilidad');
    }
  };

  const handleModify = async () => {
    if (!selectedSlot) {
      toast.error('Selecciona un horario');
      return;
    }

    setSubmitting(true);
    try {
      // Primero cancelar la reserva actual
      await api.get(`/bookings/cancel/${token}`);
      
      // Luego crear una nueva reserva
      const dateStr = date.toISOString().split('T')[0];
      await api.post('/bookings', {
        date: dateStr,
        time_slot: selectedSlot
      });
      
      toast.success('Reserva modificada exitosamente. Revisa tu email para confirmar el nuevo turno.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al modificar la reserva');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-AR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen py-20 px-6 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen py-20 px-6 flex items-center justify-center">
        <Card className="w-full max-w-md glass-effect border-white/10">
          <CardHeader className="text-center">
            <AlertTriangle className="h-16 w-16 mx-auto text-red-500" />
            <CardTitle className="text-2xl font-heading uppercase mt-4 text-red-500">Error</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-muted-foreground">{error}</p>
            <Button 
              className="mt-6 w-full"
              onClick={() => navigate('/booking')}
            >
              Ir a Reservas
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-20 px-6 md:px-12 lg:px-24" data-testid="booking-modify-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-heading font-bold uppercase tracking-tight mb-2">Modificar Reserva</h1>
          <p className="text-muted-foreground">Selecciona un nuevo horario para tu turno</p>
        </div>

        {originalBooking && (
          <Card className="mb-6 bg-orange-500/10 border-orange-500/30">
            <CardContent className="py-4">
              <p className="text-sm text-muted-foreground mb-1">Reserva original a modificar:</p>
              <p className="font-semibold">
                {formatDate(originalBooking.date)} - {originalBooking.time_slot} hs
              </p>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="glass-effect border-white/10">
            <CardHeader>
              <CardTitle className="font-heading uppercase flex items-center gap-2">
                <CalendarIcon className="h-5 w-5" />
                Nueva Fecha
              </CardTitle>
            </CardHeader>
            <CardContent className="flex justify-center">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                className="rounded-md border border-white/10"
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              />
            </CardContent>
          </Card>

          <Card className="lg:col-span-2 glass-effect border-white/10">
            <CardHeader>
              <CardTitle className="font-heading uppercase flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Nuevo Horario
              </CardTitle>
              <CardDescription>
                {date.toLocaleDateString('es-AR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {availability.map((slot) => {
                  const isSelected = selectedSlot === slot.time_slot;
                  const isAvailable = slot.available;
                  
                  return (
                    <button
                      key={slot.time_slot}
                      onClick={() => isAvailable && setSelectedSlot(slot.time_slot)}
                      disabled={!isAvailable}
                      className={`p-4 rounded-lg border transition-all text-center ${
                        isSelected
                          ? 'bg-primary text-primary-foreground border-primary shadow-lg scale-105'
                          : isAvailable
                          ? 'bg-white/5 border-white/10 hover:border-primary/50 hover:bg-white/10'
                          : 'bg-white/5 border-white/10 opacity-50 cursor-not-allowed'
                      }`}
                      data-testid={`modify-slot-${slot.time_slot}`}
                    >
                      <p className="text-lg font-bold">{slot.time_slot}</p>
                      <p className="text-xs mt-1">
                        {isAvailable ? 'Disponible' : 'Ocupado'}
                      </p>
                    </button>
                  );
                })}
              </div>

              {selectedSlot && (
                <div className="mt-6 p-4 bg-primary/10 border border-primary/30 rounded-lg">
                  <p className="font-semibold mb-2">Nuevo Turno Seleccionado:</p>
                  <p className="text-sm text-muted-foreground mb-4">
                    {date.toLocaleDateString('es-AR')} - {selectedSlot}
                  </p>
                  <Button
                    onClick={handleModify}
                    disabled={submitting}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider"
                    data-testid="confirm-modify-button"
                  >
                    {submitting ? 'Modificando...' : 'Confirmar Cambio'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
